import React, { useCallback, useEffect, useRef, useState } from "react";

import { GripVertical, X } from "lucide-react";
import { useDrag, useDrop } from "react-dnd";

import { cn } from "@/src/lib/utils";
import { moveItem } from "@/src/utils/sort";

import type { JSX, KeyboardEvent } from "react";

export interface SortableChipItem {
  id: string;
  label: string;
}

interface DragItem {
  id: string;
  index: number;
  originIndex: number;
  element: HTMLElement | null;
  lastSwapTargetId: string | null;
}

interface SortableChipProps {
  item: SortableChipItem;
  index: number;
  count: number;
  dragType: string;
  disabled?: boolean;
  removeLabel: string;
  roleDescription: string;
  onPreview: (fromIndex: number, toIndex: number) => void;
  onCommit: (dragged: DragItem, didDrop: boolean) => void;
  onKeyboardMove: (fromIndex: number, toIndex: number) => void;
  onRemove: (index: number) => void;
}

const KEY_STEP: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1 };
const FOCUS_RING =
  "outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50";

function SortableChip({
  item,
  index,
  count,
  dragType,
  disabled,
  removeLabel,
  roleDescription,
  onPreview,
  onCommit,
  onKeyboardMove,
  onRemove,
}: SortableChipProps): JSX.Element {
  const chipRef = useRef<HTMLDivElement>(null);
  const refocusIndexRef = useRef<number | null>(null);

  useEffect(() => {
    if (refocusIndexRef.current === index) {
      chipRef.current?.focus();
    }
    refocusIndexRef.current = null;
  });

  const [{ isDragging }, dragRef] = useDrag(
    () => ({
      type: dragType,
      item: (): DragItem => ({
        id: item.id,
        index,
        originIndex: index,
        element: chipRef.current,
        lastSwapTargetId: null,
      }),
      canDrag: !disabled,
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
      end: (dragged, monitor) => onCommit(dragged, monitor.didDrop()),
    }),
    [dragType, index, item.id, disabled, onCommit],
  );

  const [, dropRef] = useDrop<DragItem>(
    () => ({
      accept: dragType,
      hover(dragged, monitor) {
        const chip = chipRef.current;
        const pointer = monitor.getClientOffset();
        if (!chip || !pointer) {
          return;
        }
        if (dragged.id === item.id || dragged.index === index) {
          dragged.lastSwapTargetId = null; // eslint-disable-line no-param-reassign
          return;
        }
        if (dragged.lastSwapTargetId === item.id) {
          return;
        }
        const rect = chip.getBoundingClientRect();
        const draggedTop =
          dragged.element?.getBoundingClientRect().top ?? rect.top;
        const isSameRow = Math.abs(draggedTop - rect.top) < rect.height / 2;
        if (isSameRow) {
          const middleX = rect.left + rect.width / 2;
          const isNotPastMiddle =
            dragged.index < index ? pointer.x < middleX : pointer.x > middleX;
          if (isNotPastMiddle) {
            return;
          }
        }
        onPreview(dragged.index, index);
        dragged.index = index; // eslint-disable-line no-param-reassign
        dragged.lastSwapTargetId = item.id; // eslint-disable-line no-param-reassign
      },
    }),
    [dragType, index, item.id, onPreview],
  );

  dragRef(dropRef(chipRef));

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (disabled || !event.altKey || event.target !== event.currentTarget) {
      return;
    }
    const step = KEY_STEP[event.key];
    if (!step) {
      return;
    }
    event.preventDefault();
    const targetIndex = index + step;
    if (targetIndex < 0 || targetIndex >= count) {
      return;
    }
    refocusIndexRef.current = targetIndex;
    onKeyboardMove(index, targetIndex);
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- focusable chip for keyboard reordering
    <div
      ref={chipRef}
      aria-keyshortcuts="Alt+ArrowLeft Alt+ArrowRight"
      aria-label={item.label}
      aria-roledescription={roleDescription}
      data-testid="sortable-chip"
      onKeyDown={handleKeyDown}
      role="listitem"
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- focusable chip for keyboard reordering
      tabIndex={0}
      className={cn(
        "border-border bg-card flex h-8 items-center gap-1.5 rounded-md border pr-1.5 pl-1 text-sm",
        FOCUS_RING,
        !disabled && "cursor-grab active:cursor-grabbing",
        isDragging && "opacity-40",
      )}
      onKeyUp={(): void => {
        refocusIndexRef.current = null;
      }}
    >
      <GripVertical aria-hidden className="text-muted-foreground size-4" />
      <span>{item.label}</span>
      {disabled ? null : (
        <button
          aria-label={`${removeLabel} ${item.label}`}
          onClick={(): void => onRemove(index)}
          type="button"
          className={cn(
            "text-muted-foreground hover:text-foreground flex size-6 items-center justify-center rounded-sm",
            FOCUS_RING,
          )}
        >
          <X aria-hidden className="size-3.5" />
        </button>
      )}
    </div>
  );
}

interface SortableChipListProps {
  items: SortableChipItem[];
  dragType: string;
  removeLabel: string;
  roleDescription: string;
  disabled?: boolean;
  onReorder: (fromIndex: number, toIndex: number) => void;
  onRemove: (index: number) => void;
}

function SortableChipList({
  items,
  dragType,
  removeLabel,
  roleDescription,
  disabled,
  onReorder,
  onRemove,
}: SortableChipListProps): JSX.Element {
  const [previewList, setPreviewList] = useState<SortableChipItem[] | null>(
    null,
  );
  const displayedList = previewList ?? items;

  const latestRef = useRef({ items, onReorder });
  latestRef.current = { items, onReorder };

  // Stable identities: changing them mid-drag would re-register the drag source
  const handlePreview = useCallback(
    (fromIndex: number, toIndex: number): void =>
      setPreviewList((current) =>
        moveItem(current ?? latestRef.current.items, fromIndex, toIndex),
      ),
    [],
  );

  const handleCommit = useCallback(
    (dragged: DragItem, didDrop: boolean): void => {
      setPreviewList(null);
      if (didDrop && dragged.index !== dragged.originIndex) {
        latestRef.current.onReorder(dragged.originIndex, dragged.index);
      }
    },
    [],
  );

  const [, listDropRef] = useDrop<DragItem>(
    () => ({ accept: dragType }),
    [dragType],
  );
  const setListRef = useCallback(
    (node: HTMLDivElement | null): void => {
      listDropRef(node);
    },
    [listDropRef],
  );

  return (
    <div ref={setListRef} className="flex flex-wrap gap-2" role="list">
      {displayedList.map((item, index) => (
        <SortableChip
          key={item.id}
          count={displayedList.length}
          disabled={disabled}
          dragType={dragType}
          index={index}
          item={item}
          onCommit={handleCommit}
          onKeyboardMove={onReorder}
          onPreview={handlePreview}
          onRemove={onRemove}
          removeLabel={removeLabel}
          roleDescription={roleDescription}
        />
      ))}
    </div>
  );
}

export default SortableChipList;
